import React, { useContext, useEffect, useState } from "react";
import ReactTooltip from "react-tooltip";
import { Badge, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { SButton, SContainer, SFormControl } from "../../components/BasicComponents";
import AppNavbar from "../../components/Navbars/AppNavbar";
import { TeacherContext } from "../../context/TeacherContext";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toastIcon } from "../../utils/Toast";
import { isLectureLive } from "../../utils/lectureUtils";
import { SBadge, SHome } from "./Home.styles";
import { onDeleteCourse, onDeleteLecture } from "../../utils/Alert";
import Loading from "../../components/Loading/Loading";

const TeacherHomepage = () => {
  const { t } = useTranslation("", { keyPrefix: "home.teacher" });

  const history = useHistory();
  const [showCreateCourse, setShowCreateCourse] = useState(false);
  const { courses, getCourses, getCourseInfo, loading } = useContext(TeacherContext);

  useEffect(() => {
    localStorage.setItem("lastAccess", "/teacher");
    getCourses().then((courses) => {
      courses.forEach((course) => getCourseInfo(course.uuid));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <SContainer fluid fill="true">
          <Row>
            <AppNavbar
              startButtons={[
                <SButton className="pe-3" key="switch_student" onClick={() => history.push("/student")}>
                  <i className="fas fa-graduation-cap pt-1 px-2" />
                  {t("switch")}
                </SButton>,
              ]}
            />
          </Row>
          <SHome>
            <Container className="pt-5">
              <Row className="d-flex flex-row align-items-center">
                <Row className="w-auto fs-1 align-items-center flex-grow-1 fw-bold">{t("folders")}</Row>
                <Row className="w-auto fs-1">
                  <i
                    className="w-auto fas fa-folder-plus"
                    data-for="courses-tooltip"
                    data-tip={t("tooltip.create_folder")}
                    onClick={() => setShowCreateCourse(true)}
                  />
                </Row>
                <hr />
              </Row>

              {courses &&
                Object.values(courses)
                  .filter((c) => c !== undefined)
                  .map((course) => <Course key={course.uuid} course={course} />)}
            </Container>
          </SHome>
          <CreateCourseModal show={showCreateCourse} onHide={() => setShowCreateCourse(false)} />
          <ReactTooltip id="courses-tooltip" type="info" />
        </SContainer>
      )}
    </>
  );
};

const Course = ({ course }) => {
  const { t } = useTranslation("", { keyPrefix: "home.teacher" });
  const history = useHistory();

  const [expanded, setExpanded] = useState(true);
  const { deleteLecture, deleteCourse, getLectureInfo } = useContext(TeacherContext);

  const onNewLecture = (course) => {
    history.push(`/new-lecture/${course.uuid}`);
  };

  const onEditLecture = (uuid) => {
    getLectureInfo(uuid).then(() => history.push(`/update-lecture/${uuid}`));
  };

  return (
    <Row key={course.uuid} className="course-row">
      <div className="d-flex flex-row align-items-baseline justify-content-between">
        <div className="fs-2 flex-grow-1 d-flex flex-row">
          <span className="ml-2">{course.name}</span>
        </div>
        <div className="d-flex fs-5 justify-content-end">
          <i
            data-for="course-tooltip"
            data-tip={expanded ? t("tooltip.hide_lectures") : t("tooltip.show_lectures")}
            className={`fas fa-angle-${expanded ? "down" : "right"} m-1`}
            onClick={() => setExpanded((e) => !e)}
          />
          <i
            className="fas fa-trash m-1"
            onClick={() => onDeleteCourse(course, deleteCourse)}
            data-for="course-tooltip"
            data-tip={t("tooltip.delete_folder")}
          />

          <CopyToClipboard text={course.uuid} onCopy={() => toastIcon("📝", t("toast.copy_folder"))}>
            <i className="fas fa-copy m-1" data-for="course-tooltip" data-tip={t("tooltip.copy_folder")} />
          </CopyToClipboard>
          <i
            className="fas fa-plus m-1"
            onClick={() => onNewLecture(course)}
            data-for="course-tooltip"
            data-tip={t("tooltip.create_lecture")}
          />
        </div>
        <ReactTooltip id="course-tooltip" type="info" />
      </div>
      <hr />
      {expanded &&
        course.lectures &&
        Object.values(course.lectures).map((lecture, index) => (
          <React.Fragment key={index}>
            {lecture && (
              <Row className="lecture-row">
                <div className="d-flex flex-row align-items-baseline justify-content-between fs-4">
                  <div className="d-flex flex-row flex-grow-1">
                    {lecture.type === "live" ? (
                      <SBadge>
                        <i className="fas fa-clock me-1" />
                        {t("start_time", { date: new Date(lecture.startTime) })}
                      </SBadge>
                    ) : (
                      <SBadge className="text-uppercase">
                        <i className="fas fa-undo me-1" />
                        {t("flipped")}
                      </SBadge>
                    )}
                    <span>{lecture.name}</span>
                    {isLectureLive(lecture) && (
                      <Badge bg="danger" className="mb-1 mx-2 text-uppercase">
                        {t("live")}
                      </Badge>
                    )}
                  </div>
                  <div className="d-flex fs-5 flex-grow-1 justify-content-end">
                    <CopyToClipboard
                      text={`${window.location.host}/#/quick/${lecture.uuid}`}
                      onCopy={() => toastIcon("📝", t("toast.copy_lecture"))}
                    >
                      <i className="fas fa-copy m-2" data-for="lecture-tooltip" data-tip={t("tooltip.copy_lecture")} />
                    </CopyToClipboard>
                    <i
                      className="fas fa-edit m-2"
                      onClick={() => onEditLecture(lecture.uuid)}
                      data-for="lecture-tooltip"
                      data-tip={t("tooltip.update_lecture")}
                    />
                    <i
                      className="fas fa-trash m-2"
                      onClick={() => onDeleteLecture(lecture, deleteLecture)}
                      data-for="lecture-tooltip"
                      data-tip={t("tooltip.delete_lecture")}
                    />
                    <i
                      className="fas fa-play m-2"
                      onClick={() => {
                        history.push(`/teacher/lecture/${lecture.uuid}`);
                      }}
                      data-for="lecture-tooltip"
                      data-tip={t("tooltip.go")}
                    />
                    <ReactTooltip id="lecture-tooltip" type="info" />
                  </div>
                </div>
                <hr />
              </Row>
            )}
          </React.Fragment>
        ))}
    </Row>
  );
};

const CreateCourseModal = (props) => {
  const { t } = useTranslation("", { keyPrefix: "home.teacher.modal" });

  const [courseName, setCourseName] = useState("");
  const { createCourse } = useContext(TeacherContext);

  const onCreateClicked = () => {
    createCourse(courseName).then(() => props.onHide());
  };

  return (
    <Modal {...props}>
      <Modal.Header closeButton>
        <Modal.Title>{t("create_folder")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <SFormControl required type="text" placeholder="Folder name" onChange={(e) => setCourseName(e.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <SButton className="btn btn-secondary" onClick={() => props.onHide()}>
          {t("close")}
        </SButton>
        <SButton disabled={courseName.length === 0} className="btn btn-primary" onClick={onCreateClicked}>
          {t("create")}
        </SButton>
      </Modal.Footer>
    </Modal>
  );
};

export default TeacherHomepage;
